import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Wrench, Cog, Settings, ShieldCheck, Send, Upload, X } from "lucide-react";
import { api, buildWhatsAppUrl, generalWhatsAppMessage, sparePartWhatsAppMessage } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import useSEO from "@/hooks/useSEO";

// ---------------- Service ----------------
export function ServicePage() {
  const { settings } = useSettings();
  const wa = settings?.whatsapp;
  useSEO({
    title: "Service & Perawatan Mesin | Teknik Diesel Indonesia",
    description: "Layanan service, perawatan berkala, dan perbaikan mesin industri termasuk kompresor Airman, bar bender, bar cutter, dan peralatan konstruksi lainnya.",
    path: "/service",
  });
  const services = [
    { icon: Wrench, title: "Pemeriksaan Mesin", desc: "Cek kondisi mesin secara menyeluruh." },
    { icon: Cog, title: "Perawatan Berkala", desc: "Preventive maintenance mesin industri Anda." },
    { icon: Settings, title: "Perbaikan Mesin", desc: "Perbaikan kerusakan mesin dan kompresor." },
    { icon: ShieldCheck, title: "Konsultasi Kerusakan", desc: "Konsultasi awal dan diagnosa kerusakan unit." },
  ];
  return (
    <div>
      <section className="relative border-b border-[#1E293B] bg-[#161E2E]">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="font-mono-tech text-xs uppercase tracking-widest text-[#F59E0B] mb-2">Service</div>
          <h1 className="font-display font-extrabold text-white text-4xl lg:text-5xl mb-4">Service & Perawatan Mesin</h1>
          <p className="text-slate-300 max-w-3xl leading-relaxed">
            Teknik Diesel Indonesia melayani perawatan dan perbaikan mesin industri termasuk kompresor Airman, bar bender, bar cutter, dan peralatan konstruksi lainnya.
          </p>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-4 py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {services.map((s) => (
          <div key={s.title} className="bg-[#161E2E] border border-[#334155] hover:border-[#F59E0B] p-6 transition">
            <div className="w-12 h-12 bg-[#F59E0B]/10 border border-[#F59E0B]/30 flex items-center justify-center mb-4">
              <s.icon size={22} className="text-[#F59E0B]" />
            </div>
            <h3 className="font-display font-bold text-white text-lg mb-2">{s.title}</h3>
            <p className="text-sm text-slate-400 leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </section>
      <section className="max-w-7xl mx-auto px-4 pb-16">
        <div className="bg-gradient-to-r from-[#161E2E] to-[#1E293B] border border-[#334155] p-10 text-center">
          <h2 className="font-display font-extrabold text-white text-3xl mb-3">Konsultasikan Mesin Anda</h2>
          <p className="text-slate-400 mb-6 max-w-2xl mx-auto">Hubungi tim service kami untuk konsultasi kebutuhan perawatan dan perbaikan mesin industri Anda.</p>
          <a href={buildWhatsAppUrl(wa, generalWhatsAppMessage())} target="_blank" rel="noopener noreferrer" className={wa ? "" : "hidden"}>
            <Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm h-12 px-8 uppercase tracking-wider font-bold text-sm" data-testid="service-whatsapp-cta">Konsultasi via WhatsApp</Button>
          </a>
        </div>
      </section>
    </div>
  );
}

// ---------------- Spare Parts ----------------
export function SparePartsPage() {
  const { settings } = useSettings();
  const wa = settings?.whatsapp;
  useSEO({
    title: "Spare Parts | Teknik Diesel Indonesia",
    description: "Layanan pengadaan spare parts untuk kompresor Airman, bar bender, bar cutter, dan mesin industri lainnya di Teknik Diesel Indonesia.",
    path: "/spare-parts",
  });
  const [form, setForm] = useState({ name: "", whatsapp: "", brand: "", machine_model: "", part_name: "", part_number: "", description: "" });
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/enquiries/spare-parts/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachments((a) => [...a, data.url]);
      toast.success("Foto berhasil diupload");
    } catch {
      toast.error("Gagal upload foto");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.whatsapp || !form.brand || !form.part_name) {
      toast.error("Mohon lengkapi field yang wajib (*)");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries/spare-parts", { ...form, attachments });
      toast.success("Permintaan spare part berhasil dikirim!");
      setForm({ name: "", whatsapp: "", brand: "", machine_model: "", part_name: "", part_number: "", description: "" });
      setAttachments([]);
    } catch {
      toast.error("Gagal mengirim");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="relative border-b border-[#1E293B] bg-[#161E2E]">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="font-mono-tech text-xs uppercase tracking-widest text-[#F59E0B] mb-2">Spare Parts</div>
          <h1 className="font-display font-extrabold text-white text-4xl lg:text-5xl mb-4">Spare Parts Mesin & Kompresor</h1>
          <p className="text-slate-300 max-w-3xl leading-relaxed">Kirim permintaan spare part Anda. Tidak mengetahui nomor part? Kirim foto mesin, nameplate, atau spare part kepada kami.</p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 py-14">
        <div className="bg-[#161E2E] border border-[#334155] p-6 lg:p-8">
          <h2 className="font-display font-bold text-white text-2xl mb-6">Formulir Permintaan Spare Part</h2>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Nama *" value={form.name} onChange={(v) => setForm({...form, name: v})} testid="sp-name" required />
            <FormField label="Nomor WhatsApp *" value={form.whatsapp} onChange={(v) => setForm({...form, whatsapp: v})} testid="sp-whatsapp" required />
            <FormField label="Brand Mesin *" value={form.brand} onChange={(v) => setForm({...form, brand: v})} testid="sp-brand" required placeholder="Airman, Takeda, Toyo..." />
            <FormField label="Model Mesin" value={form.machine_model} onChange={(v) => setForm({...form, machine_model: v})} testid="sp-model" placeholder="PDS 175S, B32, dll" />
            <FormField label="Nama Part *" value={form.part_name} onChange={(v) => setForm({...form, part_name: v})} testid="sp-part-name" required />
            <FormField label="Nomor Part" value={form.part_number} onChange={(v) => setForm({...form, part_number: v})} testid="sp-part-number" />
            <div className="md:col-span-2">
              <label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400 mb-1.5 block">Deskripsi</label>
              <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={3} className="w-full bg-[#0B0F17] border border-[#334155] focus:border-[#F59E0B] px-3 py-2 text-sm text-slate-100 outline-none" data-testid="sp-description" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400 mb-1.5 block">Upload Foto (Mesin/Nameplate/Part)</label>
              <label className="border-2 border-dashed border-[#334155] hover:border-[#F59E0B] bg-[#0B0F17] p-6 text-center cursor-pointer block">
                <Upload size={20} className="mx-auto mb-2 text-slate-500" />
                <div className="text-sm text-slate-400">Klik untuk upload foto</div>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid="sp-upload" />
              </label>
              {attachments.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-3">
                  {attachments.map((a, i) => (
                    <div key={i} className="relative aspect-square border border-[#334155]">
                      <img src={a.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${a}` : a} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => setAttachments(attachments.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-rose-500 text-white w-6 h-6 flex items-center justify-center"><X size={12} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="md:col-span-2 flex flex-col sm:flex-row gap-3">
              <Button type="submit" disabled={submitting} className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0B0F17] rounded-sm h-11 px-6 uppercase tracking-wider font-bold text-sm" data-testid="sp-submit">
                <Send size={14} /> {submitting ? "Mengirim..." : "Cari Spare Part"}
              </Button>
              <a href={buildWhatsAppUrl(wa, sparePartWhatsAppMessage())} target="_blank" rel="noopener noreferrer" className={wa ? "" : "hidden"}>
                <Button type="button" className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm h-11 px-6 uppercase tracking-wider font-bold text-sm">Chat WhatsApp</Button>
              </a>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

// ---------------- About ----------------
export function AboutPage() {
  useSEO({
    title: "Tentang Kami | Teknik Diesel Indonesia",
    description: "Profil Teknik Diesel Indonesia — General Technical Supply. Penyedia kompresor, bar bender/cutter, spare parts, dan layanan service mesin industri.",
    path: "/tentang-kami",
  });
  const sections = [
    { title: "Tentang Kami", text: "Teknik Diesel Indonesia menyediakan berbagai kebutuhan mesin dan peralatan industri, termasuk portable air compressor, bar bender, bar cutter, bar roller, spare parts, serta kebutuhan pendukung industri lainnya." },
    { title: "Produk Kami", text: "Kami menyediakan kompresor Airman PDS, bar bender dan bar cutter Takeda serta Toyo, bar roller Toyo, cairan anti api Akurad, dan berbagai spare parts mesin industri." },
    { title: "Komitmen Kami", text: "Kami berkomitmen untuk menyediakan mesin dan peralatan industri yang tepat sesuai dengan kebutuhan proyek pelanggan, didukung dengan layanan purna jual yang responsif." },
    { title: "Mengapa Memilih Kami", text: "Pilihan mesin yang beragam, dukungan teknis, ketersediaan spare parts, serta pelayanan yang berorientasi pada kebutuhan pelanggan menjadi keunggulan kami." },
    { title: "Area Pelayanan", text: "Kami melayani pelanggan dari berbagai wilayah di Indonesia dengan dukungan pengiriman untuk kebutuhan mesin dan peralatan industri." },
  ];
  return (
    <div>
      <section className="relative border-b border-[#1E293B] bg-[#161E2E]">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="font-mono-tech text-xs uppercase tracking-widest text-[#F59E0B] mb-2">Tentang Kami</div>
          <h1 className="font-display font-extrabold text-white text-4xl lg:text-5xl mb-4">Tentang Teknik Diesel Indonesia</h1>
        </div>
      </section>
      <div className="max-w-4xl mx-auto px-4 py-14 space-y-8">
        {sections.map((s) => (
          <div key={s.title} className="border-l-2 border-[#F59E0B] pl-5">
            <h2 className="font-display font-bold text-white text-2xl mb-3">{s.title}</h2>
            <p className="text-slate-300 leading-relaxed">{s.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------- Contact ----------------
export function ContactPage() {
  const { settings } = useSettings();
  useSEO({
    title: "Hubungi Kami | Teknik Diesel Indonesia",
    description: "Hubungi Teknik Diesel Indonesia untuk konsultasi produk, spare parts, dan layanan service mesin industri.",
    path: "/hubungi-kami",
  });
  const s = settings || {};
  const [form, setForm] = useState({ name: "", whatsapp: "", email: "", company: "", city: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.whatsapp || !form.message) {
      toast.error("Mohon lengkapi Nama, WhatsApp, dan Pesan");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries/contact", form);
      toast.success("Pesan berhasil dikirim!");
      setForm({ name: "", whatsapp: "", email: "", company: "", city: "", subject: "", message: "" });
    } catch {
      toast.error("Gagal mengirim");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div>
      <section className="relative border-b border-[#1E293B] bg-[#161E2E]">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="font-mono-tech text-xs uppercase tracking-widest text-[#F59E0B] mb-2">Hubungi Kami</div>
          <h1 className="font-display font-extrabold text-white text-4xl lg:text-5xl mb-4">Hubungi Kami</h1>
        </div>
      </section>
      <div className="max-w-7xl mx-auto px-4 py-14 grid lg:grid-cols-2 gap-10">
        <div>
          <h2 className="font-display font-bold text-white text-2xl mb-6">Kontak Langsung</h2>
          <div className="space-y-4 mb-8">
            {s.address && <ContactItem label="Alamat" value={s.address} />}
            {s.phone && <ContactItem label="Telepon" value={s.phone} />}
            {s.whatsapp && <ContactItem label="WhatsApp" value={s.whatsapp} />}
            {s.email && <ContactItem label="Email" value={s.email} />}
            {s.business_hours && <ContactItem label="Jam Operasional" value={s.business_hours} />}
            {!s.address && !s.phone && !s.whatsapp && !s.email && (
              <div className="p-4 bg-[#F8F8F8] border-l-4 border-[#FF9418] rounded text-sm text-[#555555]">
                Informasi kontak akan segera tersedia. Silakan gunakan formulir di samping untuk mengirim pesan kepada tim Teknik Diesel Indonesia.
              </div>
            )}
          </div>
          {s.google_maps_embed && (
            <div className="border border-[#334155] aspect-video" dangerouslySetInnerHTML={{__html: s.google_maps_embed}} />
          )}
        </div>
        <div>
          <h2 className="font-display font-bold text-white text-2xl mb-6">Kirim Pesan</h2>
          <form onSubmit={submit} className="bg-[#161E2E] border border-[#334155] p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Nama *" value={form.name} onChange={(v) => setForm({...form, name: v})} testid="c-name" required />
            <FormField label="WhatsApp *" value={form.whatsapp} onChange={(v) => setForm({...form, whatsapp: v})} testid="c-whatsapp" required />
            <FormField label="Email" value={form.email} onChange={(v) => setForm({...form, email: v})} testid="c-email" type="email" />
            <FormField label="Perusahaan" value={form.company} onChange={(v) => setForm({...form, company: v})} testid="c-company" />
            <FormField label="Kota" value={form.city} onChange={(v) => setForm({...form, city: v})} testid="c-city" />
            <FormField label="Subjek" value={form.subject} onChange={(v) => setForm({...form, subject: v})} testid="c-subject" />
            <div className="md:col-span-2">
              <label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400 mb-1.5 block">Pesan *</label>
              <textarea value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} rows={4} className="w-full bg-[#0B0F17] border border-[#334155] focus:border-[#F59E0B] px-3 py-2 text-sm text-slate-100 outline-none" data-testid="c-message" required />
            </div>
            <div className="md:col-span-2">
              <Button type="submit" disabled={submitting} className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0B0F17] rounded-sm h-11 px-6 uppercase tracking-wider font-bold text-sm" data-testid="c-submit">
                <Send size={14} /> {submitting ? "Mengirim..." : "Kirim Pesan"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ---------------- 404 ----------------
export function NotFoundPage() {
  useSEO({
    title: "Halaman tidak ditemukan | Teknik Diesel Indonesia",
    description: "Halaman yang Anda cari tidak tersedia atau telah dipindahkan.",
    noindex: true,
  });
  return (
    <div className="max-w-3xl mx-auto px-4 py-24 text-center">
      <div className="text-8xl font-display font-black text-[#F59E0B] mb-3">404</div>
      <h1 className="font-display font-bold text-white text-3xl mb-3">Halaman tidak ditemukan</h1>
      <p className="text-slate-400 mb-6">Halaman yang Anda cari tidak tersedia atau telah dipindahkan.</p>
      <div className="flex gap-3 justify-center">
        <Link to="/"><Button className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0B0F17] rounded-sm">Kembali ke Beranda</Button></Link>
        <Link to="/produk"><Button variant="outline" className="border-[#334155] text-slate-200 rounded-sm">Lihat Produk</Button></Link>
      </div>
    </div>
  );
}

// helpers
function FormField({ label, value, onChange, testid, type = "text", required, placeholder }) {
  return (
    <div>
      <label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400 mb-1.5 block">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} placeholder={placeholder}
        className="w-full bg-[#0B0F17] border border-[#334155] focus:border-[#F59E0B] px-3 py-2 text-sm text-slate-100 outline-none" data-testid={testid} />
    </div>
  );
}

function ContactItem({ label, value }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="text-xs font-mono-tech uppercase tracking-wider text-[#F59E0B]">{label}</div>
      <div className="text-white">{value}</div>
    </div>
  );
}
